import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Trans } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';



const variants = {
    dropIn: {
        hidden: {
            y: '-100vh',
            opacity: 0,
        },
        visible: {
            y: '0',
            opacity: 1,
            transition: {
                duration: 0.1,
                type: 'spring',
                damping: 25,
                stiffness: 500,
            },
        },
        exit: {
            y: '100vh',
            opacity: 0,
        },
    },

    flip: {
        hidden: {
            transform: 'scale(0) rotateX(-360deg)',
            opacity: 0,
            transition: {
                delay: 0.3,
            },
        },
        visible: {
            transform: ' scale(1) rotateX(0deg)',
            opacity: 1,
            transition: {
                duration: 0.5,
            },
        },
        exit: {
            transform: 'scale(0) rotateX(360deg)',
            opacity: 0,
            transition: {
                duration: 0.5,
            },
        },
    },

    newspaper: {
        hidden: {
            transform: 'scale(0) rotate(720deg)',
            opacity: 0,
            transition: {
                delay: 0.3,
            },
        },
        visible: {
            transform: ' scale(1) rotate(0deg)',
            opacity: 1,
            transition: {
                duration: 0.5,
            },
        },
        exit: {
            transform: 'scale(0) rotate(-720deg)',
            opacity: 0,
            transition: {
                duration: 0.3,
            },
        },
    },

    badSuspension: {
        hidden: {
            y: '-100vh',
            opacity: 0,
            transform: 'scale(0) rotateX(-360deg)',
        },
        visible: {
            y: '-25vh',
            opacity: 1,
            transition: {
                duration: 0.2,
                type: 'spring',
                damping: 15,
                stiffness: 500,
            },
        },
        exit: {
            y: '-100vh',
            opacity: 0,
        },
    },

    scale: {
        hidden: {
            opacity: 0,
            scale: 0,
        },
        visible: {
            opacity: 1,
            scale: 1,
            transition: {
                duration: 0.2,
                ease: 'easeIn',
            },
        },
        exit: {
            opacity: 0,
            scale: 0,
            transition: {
                duration: 0.15,
                ease: 'easeOut',
            },
        },
    },

    fade: {
        hidden: {
            opacity: 0,
        },
        visible: {
            opacity: 1,
            transition: {
                duration: 0.4,
                ease: 'easeIn',
            },
        },
        exit: {
            opacity: 0,
            transition: {
                duration: 0.15,
                ease: 'easeOut',
            },
        },
    },
}


const closeButtonStyle = {
    position: 'absolute',
    top: '2px',
    left: '-15px',
    fontSize: '40px',
    transform: 'none',
    color: 'white',
    height: '30px',
    width: '30px',
    background: 'var(--black)',
    padding: '15px',
    borderRadius: '50%',
};


const backdropStyle = {
    position: 'fixed',
    top: '0',
    left: '0',
    width: "100%",
    maxWidth: "100%",
    maxHeight: "100vh",
    height: "100vh",
    backgroundColor: 'var(--darg-bg, hsla(0, 0 %, 19 %, 0.913))',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 10000,
    overflowY: 'auto'
}

const modalStyle = {
    minHeight: 'fit-content',
    margin: 'auto',
    paddingInline: '2px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    justifyContent: 'center',
    background: 'none',
    position: 'relative',
    color: 'var(--black)',
    opacity: 1,
};

const Backdrop = ({ children, onClick }) => {
    return (
        <motion.div
            style={backdropStyle}
            className="backdrop"
            onClick={onClick}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
        >
            {children}
        </motion.div>
    );
};

const Modal = ({ handleClose, content, type }) => {


    return (
        <Backdrop onClick={handleClose}>
            <ModalButton onClick={handleClose} label="Close" />

            <motion.div
                onClick={(e) => e.stopPropagation()} // Prevent click from closing modal
                className="modal video-modal-video"
                id="modal"
                style={modalStyle}
                variants={variants[type]}
                initial="hidden"
                animate="visible"
                exit="exit"
            >
                {content?.commonVidoe &&
                    <video
                        style={{ objectFit: 'contain', maxHeight: '99vh' }}
                        src={`https://www.lnet.co.il/Media/${content.commonVidoe}.mp4`}
                        // src='https://static.lnet.org.il/dev/virtualhighschool/כניסה למערכת הגפן.mp4'
                        preload="none"
                        title={content.text}
                        poster={content.vidoePoster}
                        controls={true}
                        autoPlay={false}
                        className="video-modal-video"
                    />}
            </motion.div>


        </Backdrop>
    );
};

export const ModalButton = ({ onClick, style }) => (
    <motion.button
        className="modal-button video-modal-close"
        style={(style || {})}
        type="button"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        onClick={onClick}
        aria-label='סגור סרטון'
    >
        <FontAwesomeIcon icon={faXmark} />
    </motion.button>
);

const useModal = () => {
    const [modalOpen, setModalOpen] = useState(false);


    const open = () => {
        document.body.style.overflow = 'visible !important';
        setModalOpen(true);
    };

    const close = () => {
        document.body.style.overflow = '';
        setModalOpen(false);
    };
    return { modalOpen, close, open };
};


const ModalContainer = ({ children, label }) => (
    // Enables the animation of components that have been removed from the tree
    <AnimatePresence
        // Disable any initial animations on children that
        // are present when the component is first rendered
        initial={false}
        // Only render one component at a time.
        // The exiting component will finish its exit
        // animation before entering component is rendered
        exitBeforeEnter={true}
        // Fires when all exiting nodes have completed animating out
        onExitComplete={() => console.log(label)}
    >
        {children}
    </AnimatePresence>
);



const ModalOpenButton = ({ modalType, content, triggerButton = onClick => <button onClick={onClick}>open modal</button> }) => {
    const { modalOpen, close, open } = useModal();
    return (
        <>
            <ModalContainer>
                {modalOpen && (
                    <Modal
                        modalOpen={modalOpen}
                        content={content}
                        type={modalType}
                        handleClose={close}
                    />
                )}
            </ModalContainer>
            {triggerButton(open)}
        </>
    )
};
export default ModalOpenButton;